import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Spinner, Form, ListGroup } from 'react-bootstrap';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import { issuesApi } from '../../../api/issuesApi';
import { IssueWithBuilding } from '../../../types/issues';
import { issueComponentCategoryText } from '../../../Helpers/issueUtils';
import IssueAssessmentBadge from './IssueAssessmentBadge';

interface ArchivedIssuesModalProps {
    show: boolean;
    onHide: () => void;
}

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

/**
 * Modal displaying archived issues (felanmälningar) with year filtering
 */
const ArchivedIssuesModal: React.FC<ArchivedIssuesModalProps> = ({
    show,
    onHide
}) => {
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [issues, setIssues] = useState<IssueWithBuilding[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!show) return;

        let isMounted = true;

        const loadIssues = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await issuesApi.getArchiveByYear(selectedYear);
                if (isMounted) setIssues(data);
            } catch (err) {
                console.error('Error loading archived issues:', err);
                if (isMounted) setError('Kunde inte ladda arkiverade felanmälningar');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadIssues();

        return () => {
            isMounted = false;
        };
    }, [show, selectedYear]);

    const formatDate = (dateStr?: string): string => {
        if (!dateStr) return '-';
        const [datePart] = dateStr.split(' ');
        return new Date(datePart).toLocaleDateString('sv-SE');
    };

    const truncateDescription = (text: string, maxLength: number = 100): string => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Arkiverade felanmälningar</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">År</Form.Label>
                    <Form.Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                        style={{ maxWidth: 120 }}
                    >
                        {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Laddar...</span>
                    </div>
                ) : error ? (
                    <div className="text-center text-danger py-4">
                        {error}
                    </div>
                ) : issues.length === 0 ? (
                    <div className="text-center text-muted py-4">
                        Inga arkiverade felanmälningar för {selectedYear}
                    </div>
                ) : (
                    <ListGroup variant="flush">
                        {issues.map(issue => (
                            <ListGroup.Item
                                key={issue.key}
                                className="d-flex justify-content-between align-items-start py-3"
                            >
                                <div className="flex-grow-1 me-3">
                                    <div className="fw-medium">
                                        {truncateDescription(issue.description)}
                                    </div>
                                    <div className="small text-muted">{issue.buildingName}</div>
                                    {issue.componentCategory && (
                                        <div className="small text-muted d-flex align-items-center gap-1 mt-1">
                                            <Tag size={12} />
                                            {issueComponentCategoryText(issue.componentCategory)}
                                        </div>
                                    )}
                                    <div className="small text-muted d-flex align-items-center gap-1 mt-1">
                                        <Calendar size={12} />
                                        Arkiverad: {formatDate(issue.archivedAt)}
                                    </div>
                                    {issue.archiveReason && (
                                        <div className="small text-muted mt-1 fst-italic">
                                            "{issue.archiveReason}"
                                        </div>
                                    )}
                                    <IssueAssessmentBadge issue={issue} showDetails className="mt-1" />
                                </div>
                                <div>
                                    {issue.convertedToWorkOrderKey ? (
                                        <Badge bg="info" className="d-flex align-items-center gap-1">
                                            <ArrowRight size={12} />
                                            Ärende
                                        </Badge>
                                    ) : (
                                        <Badge bg="secondary">
                                            Arkiverad
                                        </Badge>
                                    )}
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Stäng
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ArchivedIssuesModal;
